import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {first, take} from 'rxjs/operators';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {Ofwca} from '../interface/ofwca';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {ListResponse} from '../interface/response/list-response';
import {Contract} from '../interface/contract';
import {Document as DocumentInterface} from '../interface/document';
import {Krk} from '../interface/krk';
import {Uuid} from '../interface/response/uuid';
import {ReportCustom} from "../interface/report-custom";
import {Note as NoteInterface} from "../interface/note";
import {Registration} from "../interface/registration";
import {AffiliateCode} from "../interface/affiliateCode";
import {HiperioRegistrationConfig} from "../interface/hiperio-registration-config";

@Injectable({
  providedIn: 'root'
})
export class OfwcaService {
  private readonly ofwcaListSubject: ReplaySubject<CountedAndPaginated<Ofwca>> = new ReplaySubject<CountedAndPaginated<Ofwca>>(1);
  private readonly ofwcaNotCountedListSubject: ReplaySubject<ListResponse<Ofwca>> = new ReplaySubject<ListResponse<Ofwca>>(1);
  private readonly ofwcaMissingDocumentsSubject: ReplaySubject<CountedAndPaginated<Ofwca>> = new ReplaySubject<CountedAndPaginated<Ofwca>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, ofwcaId?: string): Observable<any> {
    if (ofwcaId) {
      return this.httpClient.put(`/rest/crm/ofwca/${ofwcaId}`, formData)
        .pipe(
          take(1)
        );
    } else {
      return this.httpClient.post('/rest/crm/ofwca', formData)
        .pipe(
          take(1)
        );
    }
  }

  public loadList(page: number, pageSize: number, filters: object, subject?: Subject<CountedAndPaginated<Ofwca>>,
                  sort?: { sortBy?: string, sortOrder?: string}): void {
    this.httpClient.get(`/rest/crm/ofwca?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Ofwca>) => {
        if (subject) {
          subject.next(result);
        } else {
          this.ofwcaListSubject.next(result);
        }
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<Ofwca>>): void {
    this.httpClient
      .get(`/rest/crm/ofwca?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Ofwca>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.ofwcaNotCountedListSubject.next(result);
        }
      });
  }


  public loadMissingDocumentsList(page: number, pageSize: number, filters: object): void {
    this.httpClient
      .get(`/rest/crm/ofwcaMissingDocuments?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<Ofwca>) => {
        this.ofwcaMissingDocumentsSubject.next(result);
      });
  }

  public getOfwcaListSubject(): ReplaySubject<CountedAndPaginated<Ofwca>> {
    return this.ofwcaListSubject;
  }

  public getOfwcaNotCountedListSubject(): ReplaySubject<ListResponse<Ofwca>> {
    return this.ofwcaNotCountedListSubject;
  }

  public getOfwcaMissingDocumentsSubject(): ReplaySubject<CountedAndPaginated<Ofwca>> {
    return this.ofwcaMissingDocumentsSubject;
  }

  public downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadRejectedCommissionReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/rejected-commission/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }


  public downloadMissingDocumentsReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwcaMissingDocuments/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  public downloadSurgeReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/method/surge-report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadSignedContractWithTheEmployeeReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/method/signed-contract-with-the-employee-report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }
  public getCustomReportSettings(reportName: string): Observable<ReportCustom> {
    return this.httpClient.get(`/rest/crm/report/custom/settings/${reportName}`)
      .pipe(first()) as Observable<ReportCustom>
      ;
  }

  public downloadCustomReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public sendCustomReportSettings(formData: object): Observable<any> {
    return this.httpClient.post('/rest/crm/report/custom/settings', formData)
      .pipe(take(1));
  }

  public downloadCustomReportRegional(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/regional/custom/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public get(ofwcaId: string): Observable<any> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}`)
      .pipe(first());
  }

  public getOfwcaContracts(ofwcaId: string): Observable<ListResponse<Contract>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/contract`)
      .pipe(first()) as Observable<ListResponse<Contract>>;
  }

  public getOfwcaRegstration(ofwcaId: string): Observable<ListResponse<Registration>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/regstration-to-cancel`)
      .pipe(first()) as Observable<ListResponse<Registration>>;
  }

  public getOfwcaContractsRegistrations(ofwcaId: string):  Observable<ListResponse<Contract>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/registration-contracts`)
      .pipe(first()) as Observable<ListResponse<Contract>>;
  }
  public getOfwcaContractsRegistrationsPartnership(ofwcaId: string):  Observable<ListResponse<Contract>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/registration-contracts-partnership`)
      .pipe(first()) as Observable<ListResponse<Contract>>;
  }
  public getOfwcaDocuments(ofwcaId: string, documentsView?: string, typeId?: number): Observable<ListResponse<DocumentInterface>> {
    const params: any = {};
    if (documentsView) {
      params.documentsView = documentsView;
    }
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/document${typeId ?  '/' + typeId : ''}`,
      {params})
      .pipe(first()) as Observable<ListResponse<DocumentInterface>>;
  }

  public getOfwcaNotes(uuid: string): Observable<ListResponse<NoteInterface>> {
    return this.httpClient.get(`/rest/crm/ofwca/${uuid}/note`)
      .pipe(first()) as Observable<ListResponse<NoteInterface>>;
  }

  public getOfwcaKrks(ofwcaId: string): Observable<ListResponse<Krk>> {
    return this.httpClient.get(`/rest/crm/ofwca/${ofwcaId}/krk`)
      .pipe(first()) as Observable<ListResponse<Krk>>;
  }

  public deleteOfwca(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/ofwca/${uuid}`)
      .pipe(first());
  }

  public ofwcaRejectedCommission(uuid: string, rejectedCommissionVal: number): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/rejected-commission/${uuid}`, {
      rejectedCommission: rejectedCommissionVal
    }).pipe(first());
  }

  public cancelRegistrations(ofwcaId: string, reason: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/registration/method/cancel`, {
      ofwca: ofwcaId,
      reason
    }).pipe(first());
  }

  changeRegional(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/${uuid}/changeRegional`, formData)
      .pipe(first());
  }

  changePartner(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/${uuid}/changePartner`, formData)
      .pipe(first());
  }

  updateActive(ofwcaId: string, formData: {active: boolean}): Observable<any> {
    return this.httpClient.put(`/rest/crm/ofwca/${ofwcaId}/active`, formData);
  }

  public changeAccountNumber(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/${uuid}/changeAccountNumber`, formData)
      .pipe(first());
  }

  public changeHiperioData(formData: any, uuid: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/${uuid}/changeHiperioData`, formData)
      .pipe(first());
  }

  public downloadRegistrationReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/registration/ofwca/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadMeetingReport(filters: object, uuid: string): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/${uuid}/meeting/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public blockRegistrations(ofwcaId: string, reason: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/registration/method/block`, {
      ofwca: ofwcaId,
      reason
    }).pipe(first());
  }
  public blockOfwca(ofwcaId: string, reason: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/method/block`, {
      ofwca: ofwcaId,
      reason
    }).pipe(first());
  }

  public unlockOfwca(ofwcaId: string): Observable<any> {
    return this.httpClient.post(`/rest/crm/ofwca/method/unlock`, {
      ofwca: ofwcaId
    }).pipe(first());
  }


  public updateCheckedContract(uuid: string, formData: { released: boolean; returned: boolean; contractUuid: string }): Observable<any> {
    return this.httpClient.patch(`/rest/crm/ofwca/${uuid}/updateChecked`, formData)
      .pipe(first()) as Observable<any>;
  }

  public sendPassword(uuid: string): Observable<any> {
    return this.httpClient.get( `/rest/crm/ofwca/${uuid}/send-password`)
      .pipe(first()) as Observable<any>;
  }

  public getAffiliateCode(uuid: string): Observable<ListResponse<AffiliateCode>> {
    return this.httpClient.get(`/rest/crm/ofwca/${uuid}/affiliateCode`)
      .pipe(first()) as Observable<ListResponse<AffiliateCode>>;
  }

  public downloadReportAffiliateLink(filters: object, uuid: string): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/ofwca/${uuid}/affiliate-link/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public getHiperioConfig(uuid: string): Observable<ListResponse<HiperioRegistrationConfig>> {
    return this.httpClient.get(`/rest/crm/ofwca/${uuid}/hiperioRegistrationConfig`)
      .pipe(first()) as Observable<ListResponse<HiperioRegistrationConfig>>;
  }

  public getByPartners(partnerUuids: string[]): Observable<ListResponse<Ofwca>> {
    return this.httpClient.get<ListResponse<Ofwca>>('/rest/crm/ofwca/by-partners',
      {
        params: {partnerUuids: partnerUuids.join(',')
        }
      }
    ).pipe(first());
  }

  public generateSet(ofwcaId: string): void {
    this.document.location.href = `/rest/crm/ofwca/${ofwcaId}/document-set`;
  }

}
